/**
 * useTeamHandles - hook partage qui charge la liste dynamique des
 * collaborateurs taguables (mentions @ et pickers d'assignation).
 *
 * Source : GET /api/users/mentionable
 * Cache module-level (1 seul fetch par session, partage entre tous les
 * composants qui utilisent ce hook).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache module-level
let _cache = null;
let _inflight = null;
const _listeners = new Set();

// Fallback statique au cas ou l'API ne repondrait pas (rare).
const FALLBACK_HANDLES = [
  { handle: 'jean', name: 'Jean (CEO)', email: 'contact@real-ease.fr', role: 'ceo', color: '#005558' },
  { handle: 'julie', name: 'Julie (Projets)', email: 'projets@real-ease.fr', role: 'julie', color: '#008A8D' },
  { handle: 'carla', name: 'Carla (CRM)', email: 'carla@real-ease.fr', role: 'carla', color: '#A57F4F' },
  { handle: 'amelie', name: 'Amelie (Admin)', email: 'admin@real-ease.fr', role: 'admin', color: '#003E40' },
];

async function _fetch() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await axios.get(`${API}/users/mentionable`);
      const data = Array.isArray(r.data) ? r.data : [];
      if (data.length > 0) {
        _cache = data;
      } else {
        _cache = FALLBACK_HANDLES;
      }
    } catch {
      _cache = FALLBACK_HANDLES;
    } finally {
      _inflight = null;
      _listeners.forEach((cb) => {
        try { cb(_cache); } catch {}
      });
    }
    return _cache;
  })();
  return _inflight;
}

export const refreshTeamHandles = () => {
  _cache = null;
  return _fetch();
};

export const useTeamHandles = () => {
  const [handles, setHandles] = useState(_cache || FALLBACK_HANDLES);

  useEffect(() => {
    let mounted = true;
    const onUpdate = (next) => {
      if (mounted && next) setHandles(next);
    };
    _listeners.add(onUpdate);

    if (!_cache) {
      _fetch().then((next) => {
        if (mounted && next) setHandles(next);
      });
    } else {
      setHandles(_cache);
    }

    return () => {
      mounted = false;
      _listeners.delete(onUpdate);
    };
  }, []);

  return handles;
};

/**
 * Helper pour retrouver le set des handles connus (utile pour la coloration
 * @mention dans le texte). Renvoie un Set<string> en lower-case.
 */
export const useKnownMentionSet = () => {
  const handles = useTeamHandles();
  // Alias historiques toujours acceptes
  const aliases = ['ceo', 'projets', 'admin'];
  return new Set([
    ...handles.map((h) => h.handle.toLowerCase()),
    ...aliases,
  ]);
};

export default useTeamHandles;
